import { Box, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import type { EditListingFormValues } from "./schema";
import { MoneyField } from "./money-field";

interface PriceFieldsProps {
  askingPrice?: number;
}

export const PriceFields = ({ askingPrice }: PriceFieldsProps) => {
  const { control, watch } = useFormContext<EditListingFormValues>();
  const actualPrice = watch("actualPrice");
  const showAskingPriceHint =
    askingPrice !== undefined &&
    askingPrice !== 0 &&
    (actualPrice === undefined || actualPrice === 0);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        Price Details
      </Typography>

      <Box>
        <Controller
          name="actualPrice"
          control={control}
          render={({ field, fieldState }) => (
            <MoneyField
              name={field.name}
              label="Unit price"
              size="small"
              fullWidth
              value={field.value}
              onChange={(v) => field.onChange(v ?? 0)}
              onBlur={field.onBlur}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
        {/* Separate caption so the hint doesn't collide with the validation error in helperText (single slot). */}
        {showAskingPriceHint && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
          >
            Asking price: {askingPrice?.toLocaleString()} EGP — set by the
            seller; the resale agent can update it.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { mobile: "1fr", tablet: "1fr 1fr" },
          gap: "12px",
        }}
      >
        <Controller
          name="downpayment"
          control={control}
          render={({ field, fieldState }) => (
            <MoneyField
              name={field.name}
              label="Down payment"
              size="small"
              fullWidth
              value={field.value}
              onChange={(v) => field.onChange(v ?? 0)}
              onBlur={field.onBlur}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="remainingInstallments"
          control={control}
          render={({ field, fieldState }) => (
            <MoneyField
              name={field.name}
              label="Remaining installments"
              size="small"
              fullWidth
              value={field.value}
              onChange={(v) => field.onChange(v ?? 0)}
              onBlur={field.onBlur}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </Box>

      <Controller
        name="maintenance"
        control={control}
        render={({ field, fieldState }) => (
          <MoneyField
            name={field.name}
            label="Maintenance fee"
            size="small"
            fullWidth
            value={field.value}
            onChange={(v) => field.onChange(v)}
            onBlur={field.onBlur}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
          />
        )}
      />
    </Box>
  );
};

export default PriceFields;
